import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../../lib/api'

const NEWS_KEY = 'news'

export interface NewsImage {
  url: string
  key: string
}

export interface NewsLink {
  label?: string
  url: string
}

export interface NewsItem {
  id: string
  tenant_id: string
  title: string
  content: string
  images: NewsImage[]
  links: NewsLink[]
  is_all_organizations: boolean
  organization_ids: string[]
  is_published: boolean
  published_at: string | null
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface NewsFilters {
  search?: string
  is_published?: boolean
  page?: number
  limit?: number
}

export interface NewsPayload {
  title: string
  content: string
  images?: NewsImage[]
  links?: NewsLink[]
  is_all_organizations?: boolean
  organization_ids?: string[]
  is_published?: boolean
}

export const useNews = (filters: NewsFilters = {}) => {
  return useQuery({
    queryKey: [NEWS_KEY, filters],
    queryFn: () =>
      apiClient.getNews(filters) as unknown as Promise<{
        items: NewsItem[]
        total: number
      }>,
    staleTime: 30 * 1000,
  })
}

export const useNewsItem = (id: string, enabled = true) => {
  return useQuery({
    queryKey: [NEWS_KEY, id],
    queryFn: () => apiClient.getNewsById(id) as Promise<NewsItem>,
    enabled: enabled && !!id,
  })
}

export const useCreateNews = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: NewsPayload) => apiClient.createNews(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NEWS_KEY] })
    },
  })
}

export const useUpdateNews = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<NewsPayload> }) =>
      apiClient.updateNews(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [NEWS_KEY] })
      queryClient.invalidateQueries({ queryKey: [NEWS_KEY, id] })
    },
  })
}

export const useDeleteNews = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.deleteNews(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NEWS_KEY] })
    },
  })
}

export const useUploadNewsImage = () => {
  return useMutation({
    mutationFn: (file: File) => apiClient.uploadNewsImage(file),
  })
}
